/**
 * @fileoverview BudgetTemplate - Budget template management component.
 *
 * Manages budget template settings including target amounts and income allocation
 * percentages for each category. Provides interface for adding/removing categories
 * from template and editing their budget parameters.
 *
 * @description Key features:
 * - Set target spending amounts per category
 * - Set income allocation percentages (must total 0% or 100%)
 * - Add/remove categories from template
 * - Visual feedback for validation (percentage totals)
 * - Read-only mode for regular members
 * - Ceased category indicators
 *
 * Percentage validation:
 * - 0%: Manual allocation mode (user allocates income manually)
 * - 100%: Automatic allocation mode (income distributed by percentages)
 * - 1-99%: Invalid (shows warning)
 *
 * @module components/BudgetTemplate/BudgetTemplate
 */
import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../../utils/moneyUtils';
import TemplateAddCategory from './TemplateAddCategory';
import TemplateCategoryCard from './TemplateCategoryCard';
import styles from './BudgetTemplate.module.css';

// Check if a category is ceased
const isCategoryCeased = (categories, categoryName) => {
    const category = categories?.find(cat => cat.categoryName === categoryName);
    return category && category.endDate;
};

// Get available categories (not ceased, not in template)
const getAvailableCategories = (categories, budgetTemplate) => {
    if (!categories || !budgetTemplate) return [];

    return categories.filter(cat =>
        !cat.endDate && // Not ceased
        !Object.keys(budgetTemplate).includes(cat.categoryName) // Not in template
    );
};

/**
 * BudgetTemplate Component
 *
 * Manages budget template settings including target amounts and percentages for each category.
 * Provides a collapsible interface for editing budget allocations.
 *
 * Props:
 * - budgetTemplate (object): Current budget template settings
 * - showTemplate (boolean): Whether template is visible
 * - totalTargetBudget (number): Calculated total of all target amounts
 * - totalPercentages (number): Calculated total of all percentages
 * - onToggleTemplate (function): Called when user toggles template visibility
 * - onUpdateTemplate (function): Called when user updates a template value
 * - categories (array): Array of category objects with endDate info
 * - onAddCategory (function): Called when user adds a category to template
 * - onRemoveCategory (function): Called when user removes a category from template
 * - userRole (string): User's role in the current budget
 * - readOnly (boolean): Whether template is read-only (for members)
 */
function BudgetTemplate({
    budgetTemplate,
    _showTemplate,
    totalTargetBudget,
    totalPercentages,
    _onToggleTemplate,
    onUpdateTemplate,
    categories,
    onAddCategory,
    onRemoveCategory,
    _userRole,
    readOnly = false
}) {
    const [showAddForm, setShowAddForm] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [error, setError] = useState('');
    const [sortedCategories, setSortedCategories] = useState([]);
    const sortTimeoutRef = useRef(null);

    // Debounced sorting to prevent jumping while typing
    useEffect(() => {
        if (!budgetTemplate) {
            setSortedCategories([]);
            return;
        }

        // Clear existing timeout
        if (sortTimeoutRef.current) {
            clearTimeout(sortTimeoutRef.current);
        }

        // Debounce sorting by 300ms
        sortTimeoutRef.current = setTimeout(() => {
            // Create lookup map for category sort orders
            const categorySortOrders = {};
            if (categories) {
                categories.forEach(cat => {
                    categorySortOrders[cat.categoryName] = cat.sortOrder || 999;
                });
            }

            const sorted = Object.entries(budgetTemplate).sort((a, b) => {
                const priorityA = a[1].priorityGroup || 1;
                const priorityB = b[1].priorityGroup || 1;

                // First: Sort by priority group
                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                }

                // Second: Sort by category sort order
                const sortOrderA = categorySortOrders[a[0]] || 999;
                const sortOrderB = categorySortOrders[b[0]] || 999;
                if (sortOrderA !== sortOrderB) {
                    return sortOrderA - sortOrderB;
                }

                // Third: Alphabetical within group
                return a[0].localeCompare(b[0]);
            });
            setSortedCategories(sorted);
        }, 300);

        // Cleanup timeout on unmount
        return () => {
            if (sortTimeoutRef.current) {
                clearTimeout(sortTimeoutRef.current);
            }
        };
    }, [budgetTemplate, categories]);

    const handleUpdateTemplate = (category, field, value) => {
        onUpdateTemplate(category, field, value);
    };

    const isPercentagesValid = totalPercentages === 0 || totalPercentages === 100;
    const availableCategories = getAvailableCategories(categories, budgetTemplate);

    const handleAddCategory = async () => {
        if (!selectedCategory) {
            setError('Please select a category');
            return;
        }

        try {
            await onAddCategory(selectedCategory);
            setSelectedCategory('');
            setShowAddForm(false);
            setError('');
        } catch (err) {
            setError(err.message || 'Failed to add category');
        }
    };

    const handleRemoveCategory = async (categoryName) => {
        const confirmed = window.confirm(
            `Are you sure you want to remove "${categoryName}" from the template?\n\n` +
            `This will not delete the category, just remove it from the template.`
        );

        if (!confirmed) return;

        try {
            await onRemoveCategory(categoryName);
            setError('');
        } catch (err) {
            setError(err.message || 'Failed to remove category');
        }
    };

    return (
        <div className={styles.container}>
            {/* Error Display */}
            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}

            {/* Add Category Section */}
            <TemplateAddCategory
                showAddForm={showAddForm}
                setShowAddForm={setShowAddForm}
                availableCategories={availableCategories}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                handleAddCategory={handleAddCategory}
                setError={setError}
                readOnly={readOnly}
            />

            {/* Template Summary */}
            <div className={styles.summary}>
                <span className={styles.summaryLabel}>Template Summary:</span>
                <span className={styles.summaryValue}>
                    Total Target: {formatCurrency(totalTargetBudget)}
                </span>
                <span className={styles.summaryDivider}>|</span>
                <span>Total Percentages: {totalPercentages}%</span>

                {!isPercentagesValid && (
                    <span className={styles.warning}>
                        ⚠️ Percentages must be 0% (manual allocation) or 100% (automatic allocation)
                    </span>
                )}
            </div>

            {/* Template Grid */}
            <div className={styles.grid}>
                {(() => {
                    // Determine if we need group separators (only if multiple groups exist)
                    const uniqueGroups = new Set(
                        Object.values(budgetTemplate).map(s => s.priorityGroup || 1)
                    );
                    const showSeparators = uniqueGroups.size > 1;

                    // Render with optional separators (using debounced sortedCategories)
                    let currentGroup = null;
                    return sortedCategories.map(([category, settings], index) => {
                        const priorityGroup = settings.priorityGroup || 1;
                        const isNewGroup = showSeparators && priorityGroup !== currentGroup;
                        currentGroup = priorityGroup;

                        return (
                            <React.Fragment key={category}>
                                {isNewGroup && (
                                    <div className={styles.groupSeparator}>
                                        <span className={styles.groupLabel}>
                                            Priority Group {priorityGroup}
                                        </span>
                                    </div>
                                )}
                                <TemplateCategoryCard
                                    category={category}
                                    settings={settings}
                                    handleUpdateTemplate={handleUpdateTemplate}
                                    handleRemoveCategory={handleRemoveCategory}
                                    isCategoryCeased={(cat) => isCategoryCeased(categories, cat)}
                                    readOnly={readOnly}
                                />
                            </React.Fragment>
                        );
                    });
                })()}
            </div>
        </div>
    );
}

export default BudgetTemplate;